import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Mail, AlertCircle, CheckCircle } from 'lucide-react';
import HandshakeLogo from '../components/HandshakeLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong');
    }
    
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#050507] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-[#10B981]/10 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#10B981]" />
          </div>
          <h1 className="font-clash text-2xl font-medium text-white mb-2">Check your email</h1>
          <p className="text-white/60 mb-8">
            If an account exists for {email}, you'll receive a password reset link shortly.
          </p>
          <Link to="/login">
            <Button variant="outline" className="border-white/10 text-white hover:bg-white/5">
              Back to login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050507] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-md bg-[#FF4500] flex items-center justify-center p-2">
            <HandshakeLogo className="w-full h-full" />
          </div>
          <span className="font-clash font-semibold text-white text-2xl">SettledInFull</span>
        </Link>

        {/* Card */}
        <div className="bg-surface p-8 rounded-md border border-white/8 card-glow">
          <div className="text-center mb-8">
            <h1 className="font-clash text-2xl font-medium text-white mb-2">Forgot password?</h1>
            <p className="text-white/60">Enter your email to receive a reset link</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 mb-6 rounded-md bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white/70">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
                  data-testid="forgot-email"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FF4500] hover:bg-[#CC3700] text-white py-5"
              data-testid="forgot-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending link...
                </>
              ) : (
                'Send reset link'
              )}
            </Button>
          </form>

          <p className="mt-6 text-center text-white/50 text-sm">
            Remember your password?{' '}
            <Link to="/login" className="text-[#FF4500] hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
